import { isJsonObject } from "../../../api/http/jsonDecoding";
import type { MemorySourceDraft } from "../../../api/threads/memoriesApi";
import type { EntryPassageRef } from "../../threads/prepare/threadIntent";
import { MAXIMUM_PASSAGES } from "./selectionPassages";

/**
 * The inputs of "Keep a memory"'s first step (ADR-0031 D5), as the web app gives them when it
 * runs the step and reads them back from the frozen request, also after a reload.
 *
 * SEAM(types-ontology): `passages` is text until ADR-0032's open types give passages a type;
 * it holds the JSON list `KeepMemoryBody.sources` takes. `origin` is `<thread id>` or
 * `<thread id>/<step key>`, as the server's keep step reads it.
 */
export const PASSAGES_INPUT = "passages";
export const ORIGIN_INPUT = "origin";

export function passagesInputText(passages: readonly EntryPassageRef[]): string {
  const sources: MemorySourceDraft[] = passages.map((passage) => ({
    entry: passage.entry,
    quote: passage.quote,
    occurrence: passage.occurrence,
  }));
  return JSON.stringify(sources);
}

export function originInputText(threadId: string, stepKey: string | null = null): string {
  return stepKey === null ? threadId : `${threadId}/${stepKey}`;
}

/** The passages a frozen `passages` input names; `null` if it holds none the server would take. */
export function readPassagesInput(text: string): readonly MemorySourceDraft[] | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (!Array.isArray(value) || value.length === 0 || value.length > MAXIMUM_PASSAGES) return null;
  const sources = value.flatMap((item: unknown) => {
    if (!isJsonObject(item)) return [];
    const { entry, quote, occurrence } = item;
    return typeof entry === "string" && typeof quote === "string" && quote !== "" && typeof occurrence === "number" && Number.isInteger(occurrence) && occurrence >= 1
      ? [{ entry, quote, occurrence }]
      : [];
  });
  return sources.length === value.length ? sources : null;
}
